import { useState, useEffect, useMemo } from "react";
import { Quote, Star } from "lucide-react";
import api from "@/lib/api";
import { useLandingConfig } from "@/hooks/useLandingConfig";

/**
 * LandingReviews — carosello recensioni.
 *
 * Fonti dati (in ordine di priorità):
 *   1. Google Places API via GET /api/public/reviews-live (source='google')
 *   2. Fallback CMS collezione `reviews` (source='cms')
 *   3. Legacy: `landing_config.texts.reviews.list` (CMS Landing)
 *   4. Fallback statico
 *
 * Le recensioni Google mostrano: avatar reviewer, nome, stelle, testo,
 * tempo relativo ("2 settimane fa") e link "Vedi su Google Maps"
 * (requisito attribution mandatorio da policy Google).
 */

const FALLBACK_REVIEWS = [
  { id: "fb-1", reviewer_name: "Marina Dattoma", text: "Eliza è un'istruttrice di pilates straordinaria, competente e profondamente attenta alle persone.", rating: 5 },
  { id: "fb-2", reviewer_name: "Luisa Carucci",  text: "Eliza é una garanzia, empatica e paziente ed energica, preparatissima e in costante evoluzione.", rating: 5 },
  { id: "fb-3", reviewer_name: "Anny Martino",   text: "Il pilates in gravidanza mi ha permesso di prevenire i tipici fastidi e di arrivare più preparata al parto.", rating: 5 },
];

export default function LandingReviews() {
  const { T, cfg } = useLandingConfig();
  const [payload, setPayload] = useState(null); // {source, reviews, place_name?, total_rating?, total_reviews?}

  useEffect(() => {
    let cancelled = false;
    api.get("/public/reviews-live")
      .then(r => { if (!cancelled) setPayload(r.data); })
      .catch(() => { if (!cancelled) setPayload({ source: "cms", reviews: [] }); });
    return () => { cancelled = true; };
  }, []);

  const source = payload?.source;
  const reviews = useMemo(() => {
    if (payload?.reviews?.length) return payload.reviews;
    const legacy = cfg?.texts?.reviews?.list;
    if (Array.isArray(legacy) && legacy.length > 0) {
      return legacy.map((r, idx) => ({
        id: r.id || `legacy-${idx}`,
        reviewer_name: r.name,
        text: r.text,
        rating: r.rating || 5,
      }));
    }
    return FALLBACK_REVIEWS;
  }, [payload, cfg]);

  const eyebrow = source === "google"
    ? `Recensioni · Google · ${payload?.total_rating?.toFixed(1) || "5.0"} ★ (${payload?.total_reviews || reviews.length})`
    : T("texts.reviews.eyebrow", "Recensioni · Google");

  const [i, setI] = useState(0);

  useEffect(() => {
    if (reviews.length <= 1) return;
    const t = setInterval(() => setI(v => (v + 1) % reviews.length), 8000);
    return () => clearInterval(t);
  }, [reviews.length]);

  useEffect(() => {
    if (i >= reviews.length) setI(0);
  }, [reviews.length, i]);

  const r = reviews[i] || reviews[0];
  if (!r) return null;

  return (
    <section data-testid="landing-reviews" className="relative bg-plum-alt text-cream">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <div className="text-[10px] uppercase tracking-[0.32em] text-cream/60 mb-6 flex items-center justify-center gap-3">
          <span className="w-8 h-px bg-cream/40" />
          {eyebrow}
          <span className="w-8 h-px bg-cream/40" />
        </div>

        <Quote className="w-10 h-10 text-primary mx-auto mb-8 opacity-70" strokeWidth={1}/>

        <blockquote key={r.id || i} className="reveal-up" data-testid={`review-card-${i}`}>
          <p className="font-serif-display italic text-2xl md:text-4xl lg:text-5xl leading-snug text-cream max-w-4xl mx-auto font-light">
            "{r.text}"
          </p>

          <footer className="mt-10 flex flex-col items-center gap-3">
            {/* Avatar + nome + stelle */}
            <div className="flex items-center gap-4">
              {r.reviewer_photo ? (
                <img
                  src={r.reviewer_photo}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="w-11 h-11 rounded-full object-cover ring-2 ring-primary/40"
                  data-testid="reviewer-avatar"
                />
              ) : (
                <div className="w-11 h-11 rounded-full bg-primary/25 text-primary font-serif-display text-xl flex items-center justify-center ring-2 ring-primary/40">
                  {(r.reviewer_name || "?").trim().charAt(0).toUpperCase()}
                </div>
              )}
              <div className="text-left">
                <div className="text-sm uppercase tracking-[0.22em] text-cream/85" data-testid="reviewer-name">
                  {r.reviewer_name || "Cliente"}
                </div>
                <div className="flex items-center gap-0.5 mt-1" aria-label={`${r.rating || 5} stelle su 5`}>
                  {Array.from({ length: 5 }).map((_, idx) => {
                    const filled = idx < Math.round(Number(r.rating) || 5);
                    return (
                      <Star
                        key={idx}
                        className={`w-3.5 h-3.5 ${filled ? "text-primary" : "text-cream/20"}`}
                        fill={filled ? "currentColor" : "none"}
                        strokeWidth={1.2}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Riga meta: quando + link Google (attribution mandatoria) */}
            {(r.relative_time || r.google_maps_uri) && (
              <div className="flex items-center gap-3 text-[11px] text-cream/55">
                {r.relative_time && <span>{r.relative_time}</span>}
                {r.google_maps_uri && (
                  <a
                    href={r.google_maps_uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:text-cream transition-colors"
                    data-testid="review-google-link"
                  >
                    Vedi su Google Maps
                  </a>
                )}
              </div>
            )}
          </footer>
        </blockquote>

        {reviews.length > 1 && (
          <div className="mt-14 flex items-center justify-center gap-2">
            {reviews.map((rv, idx) => (
              <button
                key={rv.id || `r-${idx}`}
                data-testid={`review-dot-${idx}`}
                aria-label={`Recensione ${idx + 1}`}
                onClick={() => setI(idx)}
                className={`h-px transition-all duration-500 ${
                  idx === i ? "w-10 bg-cream" : "w-4 bg-cream/25 hover:bg-cream/50"
                }`}
              />
            ))}
          </div>
        )}

        {/* Attribution Google (requisito ToS quando non c'è la Google Map visibile) */}
        {source === "google" && (
          <div className="mt-8 text-[10px] uppercase tracking-[0.22em] text-cream/45" data-testid="google-attribution">
            Powered by Google · Le recensioni provengono dal profilo Google Business e vengono aggiornate periodicamente
          </div>
        )}
      </div>
    </section>
  );
}
